import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Category, Tag, Event, fetchSiteEventsPage } from '@/services/customerService';

type View = { category: Category; tags: Tag[]; page: number; scrollY: number };
const emptyView = (): View => ({ category: { id: 0, name: 'ALL', slug: 'all' }, tags: [], page: 1, scrollY: 0 });

function readView(key: string, restore: boolean): View {
  if (!restore) return emptyView();
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (value && typeof value.category?.slug === 'string' && Array.isArray(value.tags) &&
        value.tags.every((tag: Tag) => typeof tag.slug === 'string') &&
        Number.isSafeInteger(value.page) && value.page > 0 && Number.isFinite(value.scrollY) && value.scrollY >= 0) return value;
  } catch { /* Storageが利用できない場合も一覧は取得する。 */ }
  return emptyView();
}

// 保存するのは表示条件・ページ数・位置のみ。顧客別のAPI応答は毎回再取得する。
export function useOripaEventList(siteId: number, storageKey: string, restore: boolean) {
  const [initial] = useState(() => readView(storageKey, restore));
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(initial.category);
  const [selectedTags, setSelectedTags] = useState<Tag[]>(initial.tags);
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const version = useRef(0);
  const busy = useRef(false);
  const firstLoad = useRef(true);
  const savedView = useRef<View>(initial);
  const scrollTarget = useRef<number | null>(restore ? initial.scrollY : 0);
  const loading = useRef(true);
  const retryRange = useRef<{ page: number; throughPage: number } | null>(null);

  const save = () => {
    try { sessionStorage.setItem(storageKey, JSON.stringify(savedView.current)); } catch { /* 任意のUX改善 */ }
  };

  const loadEvents = async (page = 1, throughPage = page) => {
    if (page !== 1 && busy.current) return;
    const requestVersion = page === 1 ? ++version.current : version.current;
    busy.current = true;
    loading.current = true;
    setError(null);
    if (page === 1) {
      setEventsLoading(true);
      setEvents([]);
      setNextPage(null);
      setLoadingMore(false);
    } else setLoadingMore(true);
    try {
      for (let currentPage = page; currentPage <= throughPage; currentPage += 1) {
        retryRange.current = { page: currentPage, throughPage };
        const data = await fetchSiteEventsPage(siteId, currentPage, selectedCategory?.slug, selectedTags.map(tag => tag.slug));
        if (requestVersion !== version.current) return;
        setEvents(previous => currentPage === 1 ? data.events : [
          ...previous, ...data.events.filter(event => !previous.some(existing => existing.id === event.id))
        ]);
        setNextPage(data.next_page);
        savedView.current = {
          category: selectedCategory || emptyView().category, tags: selectedTags,
          page: data.next_page === null ? currentPage : throughPage,
          scrollY: scrollTarget.current ?? savedView.current.scrollY
        };
        save();
        if (data.next_page === null) break;
      }
      retryRange.current = null;
    } catch (err: any) {
      if (requestVersion === version.current) setError(err.message || 'イベントの取得に失敗しました');
    } finally {
      if (requestVersion === version.current) {
        busy.current = false;
        loading.current = false;
        setEventsLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const retryEvents = () => {
    if (busy.current || !retryRange.current) return;
    const { page, throughPage } = retryRange.current;
    return loadEvents(page, throughPage);
  };

  useEffect(() => {
    const restorePages = firstLoad.current ? initial.page : 1;
    if (!firstLoad.current) scrollTarget.current = 0;
    firstLoad.current = false;
    loadEvents(1, restorePages);
    return () => { version.current += 1; };
  }, [selectedCategory, selectedTags, siteId]);

  useEffect(() => {
    const remember = () => {
      if (loading.current || scrollTarget.current !== null) return;
      savedView.current.scrollY = window.scrollY;
      save();
    };
    window.addEventListener('scroll', remember, { passive: true });
    window.addEventListener('pagehide', remember);
    return () => {
      window.removeEventListener('scroll', remember);
      window.removeEventListener('pagehide', remember);
    };
  }, [storageKey]);

  useLayoutEffect(() => {
    if (eventsLoading || loadingMore || error || scrollTarget.current === null) return;
    const target = scrollTarget.current;
    // ページと画像がレイアウトされてから復元する。共通ScrollRestorationとは競合させない。
    let frame = 0;
    const restorePosition = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => window.scrollTo(0, target));
    };
    const observer = new ResizeObserver(restorePosition);
    observer.observe(document.body);
    restorePosition();
    const finish = () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      scrollTarget.current = null;
      savedView.current.scrollY = window.scrollY;
      save();
    };
    const timer = window.setTimeout(finish, 2000);
    // 復元よりユーザー操作を優先する。
    window.addEventListener('wheel', finish, { once: true, passive: true });
    window.addEventListener('pointerdown', finish, { once: true, passive: true });
    window.addEventListener('touchstart', finish, { once: true, passive: true });
    window.addEventListener('keydown', finish, { once: true });
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', finish);
      window.removeEventListener('pointerdown', finish);
      window.removeEventListener('touchstart', finish);
      window.removeEventListener('keydown', finish);
    };
  }, [eventsLoading, loadingMore, error]);

  return { selectedCategory, setSelectedCategory, selectedTags, setSelectedTags,
    events, eventsLoading, nextPage, loadingMore, error, setError, loadEvents, retryEvents };
}
